<template>
  <div class="issue-card">
    <!-- 1 · IDENTITY — one text region, not two: the name ellipsises and the qty
         fragment survives, so a long catalogue title never costs her the count. -->
    <header class="issue-card__id">
      <span class="issue-card__thumb">
        <img
          v-if="avatarUrl"
          :src="avatarUrl"
          :alt="itemName"
          class="issue-card__thumb-img"
          @error="onThumbError"
        />
        <f7-icon v-else f7="cube_box_fill" size="20" class="issue-card__thumb-glyph"></f7-icon>
      </span>
      <span class="issue-card__id-text">
        <span class="issue-card__name">{{ itemName }}</span>
        <span v-if="qtyText" class="issue-card__qty">
          <span aria-hidden="true">·</span> {{ qtyText }}
        </span>
      </span>
    </header>

    <!-- READ-ONLY only. With live controls on screen the card is self-evidently
         her turn; with them gone, these two are the only thing saying where this
         stands, so they earn their place back (spec Section 2 decision 3). -->
    <turn-pill v-if="readOnly" :variant="pillVariant"></turn-pill>
    <p v-if="readOnly" class="issue-card__next-step">{{ t(nextStepKey) }}</p>

    <!-- 2-4 · THE QUESTION, its byline, and the leader's photo. -->
    <div class="issue-card__q">
      <!-- dir="auto" — this is the leader's raw text and may be wholly Latin.
           Without it the paragraph inherits RTL and the closing punctuation
           jumps to the wrong end. Adds no characters, so every assertion over
           the rendered text is unchanged.
           NO CLAMP and no "read more": truncating the question hides the thing
           she is being asked. The layout is drawn to hold a seven-line one. -->
      <p v-if="question" class="issue-card__question" dir="auto">{{ question }}</p>
      <!-- Role label, not a name, by backend recommendation (2026-07-26, ASK 5):
           a club's raw User.name is an unvetted string and the "leader" differs
           issue to issue, so the role reads truer than a name nobody reviewed
           for this surface. -->
      <span class="issue-card__by">{{ t('collection.leaderQuote.attribution') }}</span>
      <quality-photo
        v-if="photoUrl"
        compact
        :photo-url="photoUrl"
        :alt="itemName"
      ></quality-photo>
    </div>

    <!-- 5 · STAKES — what silence costs. Deliberately NO background fill: filled,
         it inverts the reading order and out-shouts the question above it. The
         inline-start rule plus the amber foreground keeps it noticed without
         becoming the headline. There is NO countdown: she has 12 hours after
         the plan closes (close-now Q-B), but the close is a manual leader action
         and the issue carries no close time, so a timer here would be a
         fabricated deadline. The sentence states the rule instead. -->
    <p v-if="showStakes" class="issue-card__stakes">{{ t('collection.answer.ifNoAnswer') }}</p>

    <!-- Her answer arrived by phone/WhatsApp/in person and a leader logged it.
         ABOVE the controls, so it reads "we wrote it down" before she re-reads
         the options she can change it to. -->
    <p v-if="showRecordedForYou" class="issue-card__hint">
      {{ t('collection.answer.recordedForYou') }}
    </p>

    <!-- 6 · THE ANSWERS. Selecting one posts NOTHING — see the submit button. -->
    <answer-actions
      v-if="!readOnly"
      :model-value="selection"
      :options="options"
      :allowed-responses="allowedResponses"
      :option-amounts="optionAmounts"
      :decline-amount="declineAmount"
      :chosen-option-index="memberOptionIndex"
      :current-response="memberResponse"
      :submitting="submitting"
      :is-collected="isCollected"
      :can-suggest-supplier="canSuggestSupplier"
      @update:model-value="onSelect"
      @suggest-supplier="emit('suggest-supplier', { selection })"
    ></answer-actions>

    <!-- 7 · THE FRAMING LINE, once per card. Her tap is ADVICE: the leader keeps
         all four close verbs on every question whatever she answered
         (InteractsWithPlanIssues.php:252-330 — "her answer prefills and
         highlights; it does not decide, and it never blocks"). This one line
         carries that for the whole card, which is precisely why the per-control
         promise sublines were cut rather than reworded. -->
    <p v-if="showAdvice" class="issue-card__advice">{{ t('collection.answer.advice') }}</p>

    <!-- The shop already on her answer, on a LIVE card too. She may be revising,
         and the fourth reply below re-opens the picker pre-selected — so what
         is on record has to be visible beside it or the picker looks empty of a
         choice she has already made. -->
    <p v-if="!readOnly && memberSupplierName" class="issue-card__answered-shop">
      {{ t('collection.answer.memberSupplierEcho', { supplier: memberSupplierName }) }}
    </p>

    <!-- 8 · The one collapsed optional. -->
    <optional-note
      v-if="showOptionalNote"
      ref="noteBlock"
      :note="memberComment"
      :photo-url="memberPhotoUrl"
      :start-open="!!memberComment || !!memberPhotoUrl"
      :disabled="submitting"
      @update:note="onNoteInput"
      @update:photo-token="onPhotoToken"
    ></optional-note>

    <!-- READ-ONLY recap of her own answer. In the revise flow the chosen control
         carries this marker itself, so it would be a duplicate there; here the
         controls are gone and this is the only record of it. -->
    <div v-if="showAnswerRecap" class="issue-card__answered">
      <span class="issue-card__answered-key">{{ t('collection.answer.current') }}</span>
      <span class="issue-card__answered-value">{{ memberAnswerLabel }}</span>
      <p v-if="memberComment" class="issue-card__answered-note">{{ memberComment }}</p>
      <!-- The shop she named inside this question, echoed back so her answer
           reads complete (brief 2026-09-15-the-shop-rides-the-answer). It is
           the NAME the backend snapshotted when she picked it, not a live
           lookup, so a shop renamed since still reads as what she chose. -->
      <p v-if="memberSupplierName" class="issue-card__answered-shop">
        {{ t('collection.answer.memberSupplierEcho', { supplier: memberSupplierName }) }}
      </p>
    </div>

    <!-- OUT OF STOCK, READ-ONLY (tips-wave P3-A, owner answer W-K). The banner
         above already swapped its wording for this line (see useItemIssue's
         readOnlyTitleKey/Body) — this is the part of that same honesty that
         belongs on the card itself: her money is still held for it, and
         cancelling is the one thing that releases it. Same held-money sentence
         `ItemSheets.vue` shows on the exact same status, reused as-is; same
         cancel button, same service call, same confirm strings, so a member
         reads one behaviour for one action wherever she meets it. -->
    <template v-if="readOnly && isOutOfStockItem">
      <p v-if="canCancelOutOfStock" class="issue-card__oos-money">
        {{ t('collection.state.outOfStockMoneyNote') }}
      </p>
      <f7-button
        v-if="canCancelOutOfStock"
        outline
        large
        color="red"
        class="issue-card__cancel-oos"
        :disabled="cancelling"
        @click="onCancelOutOfStock"
      >
        <f7-preloader v-if="cancelling" size="20" class="margin-end-half"></f7-preloader>
        <f7-icon v-else f7="xmark_circle" size="18" class="margin-end-half"></f7-icon>
        {{ t('item.status.actions.cancelOutOfStock') }}
      </f7-button>
    </template>

    <!-- HER DOOR, on a card she can no longer answer (U-29). The question is
         over; the ITEM may not be. A line the leader closed as out of stock is
         precisely the line she might know a shop for, and until now this page
         was a dead end for it: the fourth reply lives inside `answer-actions`,
         which a read-only card never mounts.

         LAST on the card on purpose — she reads what happened, then is offered
         the one thing she can still do about it. Outline, never fill: this is an
         offer, not something being asked of her, the same weight the details
         sheet gives the same door.

         Read-only ONLY. On a live card the fourth reply already carries this,
         and two doors onto one sheet is the duplication the fourth reply exists
         to avoid.

         ITS OWN LABEL, not the details sheet's «تعرف مين عنده الصنف؟». The
         banner immediately above says «مفيش حاجة مطلوبة منك دلوقتي», and a bare
         question under it reads as a fresh demand contradicting that sentence.
         «لسه» reframes the tap as a voluntary tip for next time rather than a
         reopening of a decision she has already been told is finished. -->
    <template v-if="readOnly">
      <f7-button
        v-if="canOfferSupplierDoor"
        outline
        large
        class="issue-card__suggest"
        @click="emit('suggest-supplier')"
      >
        <f7-icon f7="map_pin_ellipse" size="18" class="margin-end-half"></f7-icon>
        {{ t('item.suggestSupplier.resolvedCta') }}
      </f7-button>

      <!-- Her tip, and what the club said back — the same receipt the row and
           the details sheet show, so one suggestion reads identically wherever
           she meets it. No "change it" link here: correcting a suggestion
           belongs where the line lives, not on a closed question.

           NO LONGER `v-else-if`. It used to be the opposite half of the door,
           because the receipt only ever meant "still waiting". Now that it also
           carries the ANSWER, an answered tip renders here with the door open
           above it. -->
      <p v-if="suggestionReceipt" class="issue-card__suggest-pending">
        {{ suggestionReceipt }}
      </p>
    </template>

    <!-- THE CONFIRMATION, in place of the screen this replaced. It exists here
         rather than on its own screen because the submit button is now the
         moment of confirmation — and because flipping in place is what finally
         lets her SEE the refreshed timeline carrying her own `you-replied`
         step, which the old confirm screen threw away by replacing the card
         before it could render.

         role="status" so AT announces it without a toast; a toast would be an
         overlay, and zero overlays on the answer path is a measured bar.

         The live region is the OUTER element and it is always in the tree, empty
         until there is something to say. A region injected in the same mutation
         as its own text is frequently never announced — VoiceOver on iOS, which
         is the primary target here, is the worst offender — so building it this
         way is what makes the no-toast decision safe rather than silent. -->
    <div
      class="issue-card__confirm-live"
      :class="{ 'issue-card__confirm-live--idle': !showConfirmation }"
      role="status"
    >
      <div v-if="showConfirmation" class="issue-card__confirm">
        <f7-icon
          f7="checkmark_seal_fill"
          size="20"
          class="issue-card__confirm-icon"
        ></f7-icon>
        <div class="issue-card__confirm-body">
          <p class="issue-card__confirm-msg">{{ confirmMessage }}</p>
          <!-- The PAGE owns the next decision: it is the only layer that may
               fetch a list, and reusing `useDecisionsInbox` there is what
               guarantees the "next" offered is the same row sitting at the top
               of the inbox. This component fetches nothing. -->
          <slot name="next-decision" />
        </div>
      </div>
    </div>

    <!-- Inline blocking message — the backend's OWN member-facing Arabic (plan
         closed / not allowed / over budget), never a raw error. Sits directly
         above the SUBMIT BUTTON that failed, which is where she is looking when
         it does — it used to sit above the answer stack, which was the control
         that committed back then. -->
    <div v-if="respondError" class="issue-card__error" role="alert">
      <f7-icon f7="exclamationmark_circle_fill" size="16" class="issue-card__error-icon"></f7-icon>
      <div class="issue-card__error-body">
        <span>{{ respondError }}</span>
        <f7-button
          v-if="actionsLocked"
          small
          outline
          class="issue-card__error-contact"
          @click="contactSellersClubAdmin"
        >{{ t('profile.contactSupport') }}</f7-button>
      </div>
    </div>

    <!-- SUBMIT — in flow, never a fixed toolbar. A fixed bar would be an overlay
         on the answer path and would fight the iOS keyboard while she types the
         note. Spec Section 3.3 keeps `position: sticky` on a footer strip INSIDE the
         card as the escalation if it measures below the fold on the worst
         realistic card (7-line question + 4 photo options + open note, 375px).
         THAT MEASUREMENT HAS NOT BEEN TAKEN — the surface is still behind
         `member_issues_enabled`, so it needs a stub harness in a real browser.
         Not built speculatively, per the spec; not claimed as measured either.

         `aria-disabled` + a guard in the handler, NOT the native attribute: a
         native `disabled` button is unfocusable, so the moment a send succeeds
         focus would fall to <body> — off the very control she just pressed, and
         away from the confirmation above it. This is the same convention the
         rest of the card follows. (F7's own `disabled` prop is class-only and
         would need this guard regardless, which is why this is a plain button
         rather than an f7-button.) -->
    <button
      v-if="!readOnly"
      type="button"
      class="issue-card__submit"
      :aria-disabled="canSubmit ? undefined : 'true'"
      @click="onSubmit"
    >
      <f7-preloader
        v-if="submitting"
        size="20"
        class="issue-card__submit-spinner"
      ></f7-preloader>
      {{ t(submitLabelKey) }}
    </button>

    <!-- The answer stays editable until the leader applies it, so say so rather
         than leaving a re-tap feeling like a mistake. -->
    <p v-if="showEditHint" class="issue-card__hint">{{ t('collection.answer.editHint') }}</p>
  </div>
</template>

<script setup>
/**
 * IssueCard — one item's question, owning the viewport: one decision, one
 * screen. The 2026-08-10 redesign collapsed it from a six-reason decision engine
 * into a conversation — the leader asks a `question` in their own words and
 * lists `options` they can do instead; she answers yes / no / wait, optionally
 * picking an option BY INDEX.
 *
 * ── The framing rule, which decides more here than the layout does ───────────
 * Her tap is ADVICE, not a transaction. Per
 * `../backend/app/Filament/Concerns/InteractsWithPlanIssues.php:252-330` the
 * leader's four close verbs — collect, out-of-stock, next plan, keep-as-is — are
 * available on EVERY question whatever she answered and whether she answered at
 * all: "Her answer prefills and highlights; it does not decide, and it never
 * blocks." So no line on this card may read as our promise to deliver. The
 * single `answer.advice` line under the answer stack carries that for the whole
 * card, and it is why the three per-control promise sublines were cut instead of
 * reworded — repeating a promise beside the framing line reads as two different
 * commitments.
 *
 * ── Select, then submit ──────────────────────────────────────────────────────
 * This card used to commit on tap, which made it a form with three inputs
 * (answer, note, photo) in which one input doubled as the submit button. The
 * note sat BELOW the control that committed, so she had to fill it first and
 * nothing said so; a mis-tap cost five taps to undo; and a note could never be
 * cleared. Selection is now inert and an explicit submit button sends.
 *
 * The button is dirty-tracked against what is ON RECORD — `{response,
 * optionIndex, note, photoToken}` — so after a successful send it re-enables
 * only when something has actually changed. A byte-identical resubmit is a
 * backend no-op anyway, so offering one is noise.
 *
 * That same predicate hides the inline confirmation: the instant she edits
 * anything, «تمام، عدّلنا ردك» stops describing what is on screen, so it goes.
 * One rule, not two that can drift.
 *
 * ── What this component owns, and what it does not ───────────────────────────
 * It ASSEMBLES THE PAYLOAD. AnswerActions is presentational and reports only the
 * choice; keeping the 422-sensitive rule (`option_index` required exactly when
 * options exist) in one place is the whole point of that split.
 *
 * It does NOT fetch. The next-decision link is the PAGE's, appended through the
 * `next-decision` slot inside the confirmation.
 *
 * It does NOT branch on `reason`. That still ships but is a reporting tag — the
 * member reads `question`, and re-deriving anything from the reason would
 * resurrect the coupling this redesign removed.
 *
 * ── The three states worth naming ────────────────────────────────────────────
 *   • REVISION — `memberResponse` set and the leader hasn't applied it: the
 *     chosen control is flagged, the note opens holding what she wrote, and the
 *     stakes line goes (she is not silent).
 *   • ALREADY COLLECTED — the goods are in her box. NO money anywhere and NO
 *     stakes line: silence cannot lose her an item she already has.
 *   • READ-ONLY — terminal, or the leader's court. Detail + her recorded answer,
 *     no controls, and the turn pill / next-step strip come back.
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AnswerActions from '@/components/collection/AnswerActions.vue';
import OptionalNote from '@/components/collection/OptionalNote.vue';
import QualityPhoto from '@/components/collection/QualityPhoto.vue';
import TurnPill from '@/components/collection/TurnPill.vue';
import { useCurrency } from '@/composables/useCurrency.js';
import {
  ISSUE_STATUS,
  MEMBER_RESPONSE,
  orderedResponses,
  RESPONSE_CHANNEL,
  responseLabelKey,
} from '@/constants/collectionIssue.js';
import { ItemStatus } from '@/enums/commerce/ItemStatus.js';
import {
  declineLineImpact,
  knownLineNetCents,
  optionLineImpact,
} from '@/utils/issueMoneyImpact.js';
import {
  boxCanTakeItBack,
  canSuggestSupplier as itemAcceptsSuggestion,
  hasOpenSupplierSuggestion,
  readSupplierSuggestion,
  suggestionLabel,
} from '@/utils/supplierSuggestion.js';
import { contactSellersClubAdmin } from '@/utils/supportContact.js';

const props = defineProps({
  itemName: { type: String, default: '' },
  avatarUrl: { type: String, default: null },
  // The order-box item itself — the base every money figure is derived from.
  // Passed whole rather than as scattered scalars because `optionLineImpact`
  // needs qty, price AND discount together: the discount is a flat cents figure
  // the qty shrink never touches, and dropping it overstates a Poola line by the
  // entire PoolShare-funded share.
  item: { type: Object, default: null },
  // The leader's own words. THE field on this card.
  question: { type: String, default: null },
  // The leader's photo. Allowed on ANY question now, not just quality concerns.
  photoUrl: { type: String, default: null },
  // What the leader can do instead — `{ index, label, priceCents, qty }`. Each
  // becomes its own YES control. A label may name something the catalogue has
  // never recorded; never try to resolve it.
  options: { type: Array, default: () => [] },
  allowedResponses: { type: Array, default: () => [] },
  // The issue header's own lifecycle (`ItemIssueStatus`). Read for ONE thing —
  // the supplier-suggestion gate below — because that policy arm keys on the
  // ISSUE's status, not on the item's. Every other state this card renders is
  // already resolved into `readOnly` / `memberResponse` by useItemIssue.
  issueStatus: { type: String, default: null },
  // The goods are already in her box (derived from the item's status — see
  // useItemIssue). Suppresses every figure AND the stakes line.
  isCollected: { type: Boolean, default: false },
  submitting: { type: Boolean, default: false },
  respondError: { type: String, default: null },
  // True once a refused submit is confirmed non-retryable (a `plan-closed` 409:
  // the plan was cancelled or deleted) —
  // the controls lock while `respondError` keeps the backend's own Arabic above
  // them.
  actionsLocked: { type: Boolean, default: false },
  // The server has REPORTED this round closed (a `plan-closed` 409), confirmed or not —
  // wider than `actionsLocked` on purpose. Every sentence this card writes about
  // the FUTURE is false the moment it is true, so the two are separate: the
  // buttons fail open, the claims fail closed.
  roundReportedClosed: { type: Boolean, default: false },
  // The line's CARTON, when the host can reach it — `{ status,
  // deliveredConfirmedAt }` is enough. The suggest door's fourth arm reads it:
  // a carton that has moved on cannot take the item back, so the door must not
  // be offered even while the LINE still qualifies (U-69).
  //
  // `undefined` is the escape hatch `canSuggestSupplier` keeps for a host that
  // cannot say — a cold deep link with no boxes in the store — and it yields
  // the line-only answer rather than a false `false`.
  box: { type: Object, default: undefined },
  // Her own recorded answer, note and pick (all null before she answers).
  memberResponse: { type: String, default: null },
  memberComment: { type: String, default: null },
  memberOptionIndex: { type: Number, default: null },
  // The shop she named INSIDE this question, as it was called when she named
  // it (`memberSupplierName`). Echoed back so her answer reads complete — she
  // said "try Trust", and the card should say so.
  memberSupplierName: { type: String, default: null },
  // Her OWN photo already on record, if any — a leader-relayed one or one she
  // attached herself. Read-only; see OptionalNote for why it is shown.
  memberPhotoUrl: { type: String, default: null },
  // How the CURRENT answer reached us. Anything off-platform means a leader
  // recorded it from a call/message, which is worth confirming back to her.
  responseChannel: { type: String, default: null },
  // No form at all: detail + recap only (terminal / not her court).
  readOnly: { type: Boolean, default: false },
  pillVariant: { type: String, default: 'member' },
  // An answer landed on THIS screen just now. Deliberately NOT a state on the
  // machine — the card is answerable again the instant the POST returns, so this
  // is a fact layered on 'awaiting', not a mode to leave.
  justSubmitted: { type: Boolean, default: false },
  // The interpolated confirmation line, already resolved by the page from the
  // composable's `confirmKey` (which is null when there is genuinely nothing to
  // confirm, so the page keeps guarding it).
  confirmMessage: { type: String, default: '' },
  // May she cancel this out-of-stock line RIGHT NOW? The PAGE answers it
  // (`useItemIssue`'s `canCancelOutOfStock`, the same carton gate
  // `ItemSheets.vue`'s own cancel reads) — this card only renders the button,
  // the same split `canSuggestSupplier` above keeps.
  canCancelOutOfStock: { type: Boolean, default: false },
  // A cancel POST is in flight — spins the button and blocks a second tap.
  cancelling: { type: Boolean, default: false },
});

const emit = defineEmits(['respond', 'edit', 'suggest-supplier', 'cancel-out-of-stock']);

const { t, locale } = useI18n({ useScope: 'global' });
const { formatCurrency } = useCurrency();

// The optional note, as she is typing it. OptionalNote owns the prefill/dirty
// contract; this is only what the payload needs at submit time, so it must NOT
// be fed back to that component as its `note` prop.
const noteText = ref('');
// The FilePond token for a photo she attached on THIS card, uploaded already and
// waiting to ride along with whichever answer she taps.
const photoToken = ref('');
const noteBlock = ref(null);

// What she is about to send — `{ response, optionIndex }` or null. Local and
// inert: nothing leaves this component until she presses submit.
const selection = ref(null);
// Set the moment she picks, and never cleared. From then on the server value is
// hers to override and a background refresh must not re-seed under her — the
// same contract OptionalNote keeps for the note, for the same reason.
const selectionTouched = ref(false);

// Seed from the answer on record so a REVISION opens pre-checked rather than
// forcing a re-pick. An immediate watch rather than `ref(props.memberResponse)`:
// the card mounts before the issue resolves, so reading the prop in root scope
// would prefill on a warm cache hit and silently miss on every cold load
// (`vue/no-setup-props-reactivity-loss` flags exactly this).
watch(
  () => [props.memberResponse, props.memberOptionIndex],
  ([response, optionIndex]) => {
    if (selectionTouched.value) return;
    selection.value =
      response == null ? null : { response, optionIndex: optionIndex ?? null };
  },
  { immediate: true },
);

/**
 * The note value the field has actually SHOWN her — the last one OptionalNote
 * adopted from the server, never one it refused to adopt because she was
 * mid-edit. Everything about clearing is judged against this rather than against
 * `memberComment`, and the difference is a data-loss bug:
 *
 * She opens a card with no note, types a character, deletes it. A refresh then
 * lands a note a leader relayed. OptionalNote will not adopt it — she is
 * editing — so the textarea stays empty and she never sees that note exists.
 * Judged against `memberComment` her empty field would read as a deliberate
 * clear and destroy it. Judged against what she was shown, there is nothing to
 * clear, which is the truth.
 */
const noteSeen = ref('');

/**
 * Is an empty field a CLEAR, or was it simply never touched? Only a note she has
 * been shown can be cleared, so the two cases separate on whether one was:
 *
 *   shown, emptied    → '' ≠ 'size 42'  → cleared, send an explicit ''
 *   shown, untouched  → OptionalNote adopts it, so the values match → not a clear
 *   never shown       → '' === ''       → not a clear
 *
 * Trimmed on both sides: a stored note of pure whitespace is not something she
 * can meaningfully "clear", and comparing it untrimmed against a trimmed field
 * would open every such card pre-dirtied and then silently blank it.
 */
const noteCleared = computed(
  () => noteSeen.value.trim() !== '' && noteText.value.trim() === '',
);

/**
 * Does what she is about to send differ from what the leader has? This gates the
 * SUBMIT BUTTON only — see `editedSinceSubmit` for why the confirmation must not
 * share it.
 *
 * A fresh photo token always counts: it is a new upload by definition, and there
 * is nothing on record to compare it against.
 */
const isDirty = computed(() => {
  if (selection.value == null) return false;
  if (selection.value.response !== props.memberResponse) return true;
  if ((selection.value.optionIndex ?? null) !== (props.memberOptionIndex ?? null)) return true;
  if (noteText.value.trim() !== noteSeen.value.trim()) return true;
  return photoToken.value.trim() !== '';
});

/**
 * The controls that are actually on screen right now — the same list the child
 * renders, from the same helper, so the two can never disagree.
 */
const controls = computed(() =>
  orderedResponses(props.allowedResponses, props.options, { isCollected: props.isCollected }),
);

/**
 * Can what is selected actually be SENT? This is the invariant that keeps the
 * button honest: she may only send an answer she can see selected.
 *
 * `selection` is seeded from the record and then frozen against background
 * refreshes, so it can outlive the contract it was made against — and three real
 * states reach a live button with nothing highlighted:
 *
 *  • a legacy or leader-relayed `yes` carrying NO option index, on a question
 *    that has options. Nothing matches it (guessing an option would be a
 *    fabrication), yet typing a note used to make the button live — and then
 *    `onSubmit`'s 422 guard refused it silently. A dead primary button that
 *    announces itself as actionable is worse than a disabled one.
 *  • a refresh lands a SHORTER option list because the leader edited the
 *    question. Her index no longer exists; the guard only tests for a MISSING
 *    index, so an out-of-range one sailed through into a 422 she cannot explain.
 *  • a refresh drops `wait` from `allowedResponses` while `wait` is selected.
 *
 * It also settles the collected card: `COLLECTED_LABEL_PENDING` suppresses the
 * NO control there, so a recorded NO has no control at all — and without this
 * she could re-send, by typing a note, an answer the card never showed her.
 */
const selectionIsSendable = computed(() => {
  const chosen = selection.value;
  if (chosen == null) return false;
  return controls.value.some((control) =>
    control.response === chosen.response &&
    (control.optionIndex == null
      ? chosen.optionIndex == null
      : control.optionIndex === chosen.optionIndex),
  );
});

/**
 * Has she edited since the answer landed? The confirmation reads THIS, not
 * `isDirty`, and the two genuinely need different answers in one real state:
 *
 * She sends «تأجيل», the confirmation appears, and a leader logs a phone answer
 * that the next background refresh brings in. Her selection now differs from the
 * record, so `isDirty` is true and the button correctly re-enables. But she has
 * touched nothing — so «تمام» must not vanish out from under her as though she
 * had, and the page must not be told she edited.
 */
const editedSinceSubmit = ref(false);

/** One real edit — a pick, a keystroke, a photo. Not a server value landing. */
function markEdited() {
  if (editedSinceSubmit.value) return;
  editedSinceSubmit.value = true;
  // Only worth telling the page while a confirmation is actually up.
  if (props.justSubmitted) emit('edit');
}

watch(
  () => props.justSubmitted,
  (landed) => {
    if (!landed) return;
    editedSinceSubmit.value = false;
    // Belt and braces with the `memberPhotoUrl` watch below: a token is spent
    // the moment an answer is RECORDED, and re-sending a spent one is a 422. The
    // watch alone misses a submit whose resulting URL does not change, which
    // would pin the card dirty forever and re-send the consumed token.
    photoToken.value = '';
  },
);

const canSubmit = computed(
  () =>
    !props.readOnly &&
    !props.submitting &&
    !props.actionsLocked &&
    selectionIsSendable.value &&
    isDirty.value,
);

/**
 * May she name a shop from THIS card?
 *
 * Two of the policy's three arms are reachable from here, and they have to be
 * ORed rather than collapsed, because they key on different objects:
 *
 *  - The issue is sitting in her court (`awaiting_member`). This is the arm the
 *    fourth reply exists for, and it holds regardless of the item's own status —
 *    a line being collected right now still qualifies while a question on it is
 *    unanswered.
 *  - The ITEM independently qualifies (out of stock, or still waiting). This
 *    keeps the door open on a REVISION, where the issue has moved to `open` and
 *    the first arm has stopped being true.
 *
 * Read-only wins over both: a card she cannot answer is a card whose line the
 * backend has already moved past.
 */
const canSuggestSupplier = computed(() => {
  if (props.readOnly) return false;
  // THE CARTON GATE APPLIES TO BOTH ARMS (U-69). It used to be missing here
  // while the details sheet had it, so a moved-on carton still offered the
  // fourth reply — a tap that could only be refused.
  if (!itemAcceptsSuggestion(props.item, props.box)) {
    // …except on the arm that is about the QUESTION rather than the line: a
    // question sitting in her court is still answerable, and naming a shop is
    // part of answering it. The carton still has to be able to take the goods,
    // which is why this is checked against the carton and not skipped.
    return props.issueStatus === ISSUE_STATUS.AWAITING_MEMBER
      && boxStillTakesItBack.value;
  }
  return true;
});

/** The carton half on its own, for the awaiting-member arm above. `undefined`
 *  means the host cannot say, which stays permissive — same escape hatch. */
const boxStillTakesItBack = computed(
  () => props.box === undefined || boxCanTakeItBack(props.box),
);

/**
 * The same door, on a card she can no longer answer (U-29).
 *
 * A SECOND computed rather than a relaxed `canSuggestSupplier`, because the two
 * answer different questions for different consumers: that one feeds
 * `answer-actions`, which only ever mounts on a live card, and its
 * `AWAITING_MEMBER` arm is meaningless here (a read-only card can never be
 * awaiting her — `isIssueAnswerable` returns true for that status, and
 * `useItemIssue.syncState` only rests at `read-only` when answerable is false).
 * So this is just the ITEM's own eligibility: out of stock, or still waiting and
 * not yet on a run.
 *
 * Suppressed while a suggestion is still open, exactly as the details sheet
 * suppresses it — she has already told us, and a send button beside «وصل
 * اقتراحك للفريق» reads as though the first one never registered.
 */
const suggestion = computed(() => readSupplierSuggestion(props.item));

// Only an OPEN tip shuts the door. An ANSWERED one is history — she has read
// what the club said and may well know a second shop — so the door opens again
// beneath the answer rather than staying shut behind it.
const canOfferSupplierDoor = computed(
  () => itemAcceptsSuggestion(props.item, props.box) && !hasOpenSupplierSuggestion(props.item),
);

// The SAME writer ItemCard and ItemSheets use, so one suggestion reads
// identically wherever she meets it rather than by three files agreeing.
const suggestionReceipt = computed(() =>
  suggestionLabel(suggestion.value, { t, locale: locale.value, formatMoney: formatCurrency }),
);

const submitLabelKey = computed(() =>
  props.memberResponse != null ? 'collection.answer.submitUpdate' : 'collection.answer.submit',
);

// Not on a read-only card: a leader-apply landing in the same breath as her send
// leaves the page showing a terminal banner that names what ACTUALLY happened,
// and an outcome confirmation above it would be a second, competing claim.
const showConfirmation = computed(
  () =>
    props.justSubmitted &&
    !editedSinceSubmit.value &&
    !props.readOnly &&
    Boolean(props.confirmMessage),
);

/** She picked something. Nothing is sent — this only moves the highlight and
 *  marks the selection hers, so a background refresh stops re-seeding it. */
function onSelect(value) {
  selectionTouched.value = true;
  selection.value = value;
  markEdited();
}

/** `meta.adopted` distinguishes a server value the field took up from something
 *  she typed — only the latter is an edit, and only the former updates the
 *  baseline every clearing decision is judged against. */
function onNoteInput(value, meta = {}) {
  noteText.value = value ?? '';
  if (meta.adopted) noteSeen.value = noteText.value;
  else markEdited();
}

function onPhotoToken(token) {
  photoToken.value = token ?? '';
  markEdited();
}

function onThumbError(event) {
  event.target.style.display = 'none';
}

/**
 * Is the LINE itself out of stock? Read-only ONLY (see the template) — a live
 * card already carries the real status in its answer stack, and this exists
 * solely to swap the money note + cancel button in for the ones a terminal
 * "الموضوع خلص" card would otherwise show on a line that is not actually done
 * (tips-wave P3-A, owner answer W-K). Coerced the way every other status gate
 * in this codebase is, since `item.status` can arrive as a wire string.
 */
const isOutOfStockItem = computed(() => {
  const raw = props.item?.status;
  if (raw == null) return false;
  const status = typeof raw === 'string' ? Number(raw) : raw;
  return status === ItemStatus.OUT_OF_STOCK;
});

function onCancelOutOfStock() {
  if (props.cancelling) return;
  emit('cancel-out-of-stock');
}

const qtyText = computed(() => {
  const qty = props.item?.qty;
  return typeof qty === 'number' && qty > 0 ? t('collection.orderLine.qty', { qty }) : '';
});

// Where this stands, on a read-only card only. Reuses `pillVariant` rather than
// re-deriving whose court the issue is in — it is already the SSOT and the pill
// beside it renders from the same value, so the two can never disagree.
//
// No 'member' entry: a read-only card can never carry pillVariant 'member' —
// AWAITING_MEMBER is always answerable (`isIssueAnswerable`), so that pill only
// ever appears on a live card. 'resolved' falls to its own key; every other
// pill (i.e. 'handled') falls to the shared withTeam default below.
const NEXT_STEP_KEY_BY_PILL = {
  resolved: 'collection.nextStep.finished',
};
const nextStepKey = computed(
  () => NEXT_STEP_KEY_BY_PILL[props.pillVariant] ?? 'collection.nextStep.withTeam',
);

/**
 * Is the round still open TO THIS MEMBER — i.e. may the card still make claims
 * about what she can do next? Three lines are promises about the future and all
 * three are false in exactly the same circumstances, so they share one predicate
 * rather than drifting apart. `roundReportedClosed` is wider than
 * `actionsLocked` on purpose: disabling controls fails open, but a CLAIM must
 * fail closed — without it the card spends at least one round-trip telling her
 * to answer before the round closes directly above the backend's own
 * «التجميع ده قفل خلاص».
 */
const decisionStillOpen = computed(
  () => !props.readOnly && !props.actionsLocked && !props.roundReportedClosed,
);

/**
 * The cost of silence. Three terms:
 *   • the round is still open — otherwise it promises a future that is gone;
 *   • she has not answered — repeating it after she has would tell a member who
 *     did the thing that she still hasn't;
 *   • the goods are NOT already in her box — silence cannot lose her an item she
 *     already has, so the sentence is simply untrue there.
 */
const showStakes = computed(
  () => decisionStillOpen.value && props.memberResponse == null && !props.isCollected,
);

const showEditHint = computed(() => decisionStillOpen.value && props.memberResponse != null);

// The framing line belongs to a LIVE answer stack: "your answer reaches the
// leader, and he acts on it" is a claim about the future, so it retires in
// exactly the circumstances the other two do — which is why it reads the shared
// predicate rather than re-deriving two of its three terms. It previously
// omitted `roundReportedClosed`, so a 409 whose confirming re-read failed left
// this promise sitting directly under the backend's own «التجميع ده قفل خلاص».
const showAdvice = computed(() => decisionStillOpen.value);

// Kept mounted through a refusal ONLY when there is something in it: spec Section 9
// requires an open block to survive a refusal holding her typed note, while a
// locked card she never opened it on has no reason to offer it.
const showOptionalNote = computed(() => {
  if (props.readOnly) return false;
  if (!props.actionsLocked) return true;
  return noteText.value !== '' || !!props.memberComment;
});

const showAnswerRecap = computed(() => props.readOnly && props.memberResponse != null);

// Shown in BOTH the read-only recap and the revise flow: a member who phoned her
// answer in needs to see it landed whichever state the card is in. `in_app`,
// null (unknown — a superseded revision) and any unrecognised value all stay
// silent; only a positive off-platform channel says anything.
const showRecordedForYou = computed(
  () =>
    !props.readOnly &&
    props.memberResponse != null &&
    props.responseChannel != null &&
    props.responseChannel !== RESPONSE_CHANNEL.IN_APP &&
    // An UNRECOGNISED channel stays silent too: the vocabulary is additive on
    // the backend, and claiming "a leader recorded this for you" about a value
    // we do not know is a guess about who spoke to whom.
    Object.values(RESPONSE_CHANNEL).includes(props.responseChannel),
);

// ── Money ───────────────────────────────────────────────────────────────────
// Every figure comes from `issueMoneyImpact`, which returns null rather than a
// number it cannot stand behind — so an unknowable input silently removes the
// figure instead of rendering a fabricated «0.00 ج.م». Both helpers additionally
// refuse outright on an already-collected item; the guard is repeated in the
// predicates below so the intent is legible at the call site too.
const lineTotalCents = computed(() => knownLineNetCents(props.item));

/** Formatted figures keyed by OPTION INDEX — the line each option produces. */
const optionAmounts = computed(() => {
  if (props.isCollected) return {};
  const amounts = {};
  for (const option of props.options) {
    const impact = optionLineImpact({
      item: props.item,
      option,
      isCollected: props.isCollected,
    });
    if (impact) amounts[option.index] = formatCurrency(impact.resultingCents);
  }
  return amounts;
});

/**
 * What declining takes off the order. No minus sign, deliberately: a leading `−`
 * on «{number} ج.م» is a bidi reordering risk this app has never carried, and
 * the shipped sentence already names the direction in words.
 */
const declineAmount = computed(() => {
  if (props.isCollected) return null;
  const impact = declineLineImpact({
    lineTotalCents: lineTotalCents.value,
    isCollected: props.isCollected,
  });
  return impact ? formatCurrency(impact.currentCents) : null;
});

/**
 * Her recorded answer, named with the exact words that were on the control she
 * tapped: an answer that carried an option is named by that OPTION'S label, not
 * by a key, since the label is the leader's own free text.
 */
const memberAnswerLabel = computed(() => {
  const chosen = props.options.find((o) => o.index === props.memberOptionIndex);
  if (props.memberResponse === MEMBER_RESPONSE.YES && chosen) return chosen.label;
  const key = responseLabelKey(props.memberResponse, { isCollected: props.isCollected });
  return key ? t(key) : '';
});

/**
 * Assemble the payload and send it.
 *
 * `photoToken` is OMITTED when blank, and that omission is what carries a photo
 * already on record through a revision (the 2026-08-12 backend change made
 * omission the KEEP signal).
 *
 * The note reaches the same outcome by a different route, and the distinction
 * matters to anyone tempted to "simplify" this: an untouched note is RE-SENT
 * verbatim, not omitted, because OptionalNote prefills the field with it — so
 * `note` is non-blank and the first branch takes it. Writing the identical
 * string back is a no-op, and it is also correct under the OLD semantics where
 * omission deleted. The genuinely omitted case is a blank field with nothing on
 * record. An emptied note is the one case that sends something different:
 * `noteCleared` becomes an explicit `''`, the only thing that clears one.
 *
 * The option guard is the 422 guard: a YES on a question that offers options
 * MUST carry an index. AnswerActions never renders a bare YES there, so this can
 * only fire on a malformed control — refuse rather than post a certain refusal.
 */
function onSubmit() {
  if (!canSubmit.value || selection.value == null) return;
  const { response, optionIndex } = selection.value;
  if (response === MEMBER_RESPONSE.YES && props.options.length > 0 && optionIndex == null) return;

  const payload = {};
  if (response === MEMBER_RESPONSE.YES && Number.isInteger(optionIndex)) {
    payload.optionIndex = optionIndex;
  }
  const note = noteText.value.trim();
  if (note) payload.note = note;
  else if (noteCleared.value) payload.noteCleared = true;
  const token = photoToken.value.trim();
  if (token) payload.photoToken = token;

  emit('respond', { response, payload });
}

/**
 * Forget a spent token once the answer is on record.
 *
 * The token is SINGLE-USE: re-sending it on a later revision is a guaranteed 422
 * rather than a second photo, and the member would read a refusal she cannot
 * explain. Watching `memberPhotoUrl` is what tells us the server consumed it —
 * a refused submit leaves the photo absent and the token in hand, so a retry
 * still carries it.
 */
watch(
  () => props.memberPhotoUrl,
  (url, prior) => {
    if (url && url !== prior && photoToken.value) {
      photoToken.value = '';
      noteBlock.value?.clearPhoto?.();
    }
  },
);
</script>

<style scoped>
/* Spacing comes from the flex gap, not from per-element margins — one number to
   change, and no compounding margins between conditionally-rendered blocks. */
.issue-card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: var(--ci-surface);
  border: 1px solid var(--ci-border);
  border-radius: var(--poolit-radius-xl);
  padding: 16px 16px 14px;
  box-shadow: var(--poolit-shadow-soft);
}

/* 1 · identity */
.issue-card__id {
  display: flex;
  align-items: center;
  gap: 11px;
}

.issue-card__thumb {
  width: 44px;
  height: 44px;
  flex: none;
  border-radius: var(--poolit-radius-lg);
  overflow: hidden;
  display: grid;
  place-items: center;
  background: var(--ci-surface-soft);
  color: var(--poolit-text-muted);
}

.issue-card__thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.issue-card__id-text {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 7px;
}

/* The name yields; the qty never does. */
.issue-card__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--poolit-font-size-base);
  font-weight: var(--poolit-font-weight-bold);
  line-height: 1.3;
  color: var(--poolit-text-primary);
}

.issue-card__qty {
  flex: none;
  font-size: var(--poolit-font-size-xs);
  color: var(--poolit-text-muted);
  font-variant-numeric: tabular-nums;
}

/* Read-only only — see the template. */
.issue-card__next-step {
  margin: 0;
  font-size: var(--poolit-font-size-sm);
  font-weight: var(--poolit-font-weight-medium);
  line-height: 1.5;
  color: var(--ci-brand-fg);
}

/* 2-4 · question group */
.issue-card__q {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.issue-card__question {
  margin: 0;
  font-size: var(--poolit-font-size-lg);
  font-weight: var(--poolit-font-weight-semibold);
  line-height: 1.55;
  color: var(--poolit-text-primary);
  text-wrap: pretty;
}

/* A short rule, then the role. Reads as an attribution without the weight of a
   blockquote — the question above IS the leader's words, so it needs a signature
   rather than a frame. */
.issue-card__by {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--poolit-font-size-xs);
  font-weight: var(--poolit-font-weight-regular);
  color: var(--poolit-text-muted);
}

.issue-card__by::before {
  content: '';
  flex: none;
  width: 14px;
  height: 1px;
  background: var(--ci-border);
}

/* 5 · stakes — no fill, on purpose. See the template comment. */
.issue-card__stakes {
  margin: 0;
  padding-inline-start: 11px;
  border-inline-start: 2.5px solid var(--ci-await-fg);
  font-size: var(--poolit-font-size-sm);
  font-weight: var(--poolit-font-weight-medium);
  line-height: 1.55;
  color: var(--ci-await-fg);
}

/* 7 · the framing line — quiet and centred: it frames the choice, it is not an
   instruction, and it must not compete with the controls above it. */
.issue-card__advice {
  margin: 0;
  padding: 0 4px;
  text-align: center;
  font-size: var(--poolit-font-size-xs);
  line-height: 1.5;
  color: var(--poolit-text-muted);
}

.issue-card__hint {
  margin: 0;
  font-size: var(--poolit-font-size-sm);
  line-height: 1.5;
  color: var(--poolit-text-muted);
}

/* Read-only recap of her own answer. */
.issue-card__answered {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 11px 13px;
  border-radius: var(--poolit-radius-lg);
  background: var(--ci-brand-bg);
}

.issue-card__answered-key {
  font-size: var(--poolit-font-size-xs);
  color: var(--poolit-text-secondary);
}

.issue-card__answered-value {
  font-size: var(--poolit-font-size-base);
  font-weight: var(--poolit-font-weight-bold);
  line-height: 1.4;
  color: var(--ci-brand-fg);
}

.issue-card__answered-note {
  margin: 6px 0 0;
  padding-inline-start: 10px;
  border-inline-start: 2px solid var(--ci-border);
  font-size: var(--poolit-font-size-sm);
  line-height: 1.5;
  color: var(--poolit-text-secondary);
}

/* The shop she named, set the same way as her note — it is the same kind of
   thing: a fact she added to her answer rather than one the club stated. */
.issue-card__answered-shop {
  margin: 6px 0 0;
  padding-inline-start: 10px;
  border-inline-start: 2px solid var(--ci-border);
  font-size: var(--poolit-font-size-sm);
  line-height: 1.5;
  color: var(--poolit-text-secondary);
}

/* The held-money sentence on an out-of-stock, read-only line. Same quiet
   weight as `issue-card__hint` — a fact, paired with the cancel button right
   beneath it. */
.issue-card__oos-money {
  margin: 0;
  font-size: var(--poolit-font-size-sm);
  line-height: 1.55;
  color: var(--poolit-text-secondary);
}

.issue-card__cancel-oos {
  margin: 0;
}

/* Her door on a closed question. The card is a flex column with its own gap, so
   this needs no margin of its own — only the reset F7 applies to a button in a
   non-list context. */
.issue-card__suggest {
  margin: 0;
}

/* The receipt, when the door is shut because she has already walked through it.
   Same quiet weight as `issue-card__hint` beside it — a fact, not a status. */
.issue-card__suggest-pending {
  margin: 0;
  font-size: var(--poolit-font-size-sm);
  line-height: 1.5;
  color: var(--poolit-text-secondary);
}

/* The live region is ALWAYS in the tree so AT has registered it before the
   message lands. While idle it is taken out of flow rather than hidden —
   `display: none` would drop it from the accessibility tree and put us back
   where we started, and leaving it in flow would cost a 14px card gap for an
   element with nothing in it. */
.issue-card__confirm-live--idle {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

/* The inline confirmation — the screen this replaced, folded into the card. */
.issue-card__confirm {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 12px 13px;
  border-radius: var(--poolit-radius-lg);
  background: var(--ci-ok-bg, #e1f3e8);
  color: var(--ci-ok-fg, #16964f);
}

.issue-card__confirm-icon {
  flex: none;
  margin-top: 1px;
}

.issue-card__confirm-body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.issue-card__confirm-msg {
  margin: 0;
  font-size: var(--poolit-font-size-sm);
  font-weight: var(--poolit-font-weight-medium);
  line-height: 1.55;
}

/* Submit — the one primary action on the card. Filled with --ci-brand-fill
   (primary-700, 7.29:1 with white) and NOT primary-500, which measures 4.14:1
   and fails AA in both themes — the same reason the solo YES uses it. */
.issue-card__submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  width: 100%;
  min-height: 52px;
  padding: 10px 16px;
  border: none;
  border-radius: var(--poolit-radius-lg);
  background: var(--ci-brand-fill);
  color: var(--poolit-text-on-primary);
  font-family: inherit;
  font-size: var(--poolit-font-size-base);
  font-weight: var(--poolit-font-weight-bold);
  line-height: 1.3;
  cursor: pointer;
}

.issue-card__submit:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--ci-brand-ring);
}

/* aria-disabled, not the native attribute — see the template. Kept focusable on
   purpose, so a send that succeeds does not drop focus to <body>. */
.issue-card__submit[aria-disabled='true'] {
  opacity: 0.5;
  cursor: default;
}

.issue-card__submit-spinner {
  --f7-preloader-color: currentColor;
}

/* Inline blocking message. */
.issue-card__error {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 11px 13px;
  border-radius: var(--poolit-radius-md);
  background: var(--ci-danger-bg);
  color: var(--ci-danger-fg);
  font-size: var(--poolit-font-size-sm);
  line-height: 1.55;
}

.issue-card__error-icon {
  flex: none;
  margin-top: 1px;
}

.issue-card__error-body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.issue-card__error-contact {
  --f7-button-height: 48px;
  --f7-button-font-size: var(--poolit-font-size-sm);
  --f7-button-text-color: var(--ci-danger-fg);
  --f7-button-border-color: var(--ci-danger-fg);
  padding-inline: 14px;
}
</style>
